import { useEffect, useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import { AI_SHARING_CHANGED, AI_SHARING_VERSION, getCachedAISharingStatus, requestAISharing } from '../services/aiSharingService';

type Availability = 'checking' | 'off' | 'on' | 'update' | 'error';

/** Informational only: provider work still uses the server-authoritative grant. */
export function AIAvailabilityNotice({ enabled = true }: { enabled?: boolean }) {
  const { authSession, isAuthSessionCurrent } = useApp();
  const [view, setView] = useState<{ owner: typeof authSession; availability: Availability; busy: boolean }>({ owner: null, availability: 'checking', busy: false });
  const refreshRef = useRef<(force?: boolean) => void>(() => undefined);
  const reviewRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const session = authSession;
    if (!enabled || !session) return;
    let disposed = false;
    let lookup: AbortController | null = null;
    const current = () => !disposed && !session.signal.aborted && isAuthSessionCurrent(session);
    const refresh = (force = false) => {
      if (!current()) return;
      lookup?.abort();
      const controller = new AbortController();
      lookup = controller;
      setView(previous => ({ owner: session, availability: 'checking', busy: previous.owner === session && previous.busy }));
      void getCachedAISharingStatus(controller.signal, force).then(status => {
        if (!current() || controller.signal.aborted) return;
        const availability: Availability = status.disclosure_version !== AI_SHARING_VERSION ? 'update'
          : status.allowed && status.grant_id ? 'on' : 'off';
        setView(previous => ({ owner: session, availability, busy: previous.owner === session && previous.busy }));
      }).catch(() => {
        if (current() && !controller.signal.aborted) setView(previous => ({ owner: session, availability: 'error', busy: previous.owner === session && previous.busy }));
      });
    };
    const abort = () => { lookup?.abort(); reviewRef.current?.abort(); };
    const refreshOnEvent = () => refresh();
    refreshRef.current = refresh;
    window.addEventListener(AI_SHARING_CHANGED, refreshOnEvent);
    window.addEventListener('focus', refreshOnEvent);
    session.signal.addEventListener('abort', abort, { once: true });
    refresh();
    return () => {
      disposed = true;
      abort();
      refreshRef.current = () => undefined;
      window.removeEventListener(AI_SHARING_CHANGED, refreshOnEvent);
      window.removeEventListener('focus', refreshOnEvent);
      session.signal.removeEventListener('abort', abort);
    };
  }, [enabled, authSession, isAuthSessionCurrent]);

  if (!enabled || !authSession || authSession.signal.aborted) return null;
  const availability = view.owner === authSession ? view.availability : 'checking';
  const busy = view.owner === authSession && view.busy;
  const enable = async () => {
    const session = authSession;
    if (busy || !isAuthSessionCurrent(session)) return;
    const controller = new AbortController();
    reviewRef.current?.abort();
    reviewRef.current = controller;
    setView({ owner: session, availability, busy: true });
    try {
      await requestAISharing(controller.signal);
      if (!controller.signal.aborted && isAuthSessionCurrent(session)) refreshRef.current();
    } catch {
      if (!controller.signal.aborted && isAuthSessionCurrent(session)) setView({ owner: session, availability: 'error', busy: false });
    } finally {
      if (reviewRef.current === controller) reviewRef.current = null;
      if (!controller.signal.aborted && isAuthSessionCurrent(session)) setView(previous => previous.owner === session ? { ...previous, busy: false } : previous);
    }
  };

  const labels: Record<Availability, string> = {
    checking: 'Checking AI settings…',
    off: 'AI is off',
    on: 'AI is on',
    update: 'AI setup has changed. Update Rubikoo or try again later.',
    error: 'Could not check AI settings',
  };
  return <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm" style={{ background: 'var(--app-tag-bg)', borderColor: 'var(--app-tag-border)', color: 'var(--app-text)' }}>
    <span role="status" aria-live="polite">{labels[availability]}</span>
    {availability === 'off' && <button type="button" disabled={busy} onClick={() => void enable()} className="min-h-11 rounded-lg px-3 font-medium" style={{ color: 'var(--app-accent)' }}>{busy ? 'Opening…' : 'Enable AI'}</button>}
    {(availability === 'error' || availability === 'update') && <button type="button" disabled={busy} onClick={() => refreshRef.current(true)} className="min-h-11 rounded-lg px-3 font-medium" style={{ color: 'var(--app-accent)' }}>Retry</button>}
  </div>;
}
